import { pick } from "../store/utils/lodash-replacement";

// Bitmask constants for artist rights
export const RECHT_LESEN = 1
export const RECHT_SCHREIBEN = 2
export const RECHT_LOESCHEN = 4
export const RECHT_EINNAHMENSCHREIBEN = 8

/**
 * Checks whether a rights entry satisfies all required bitmask flags.
 * @param {Object} recht - Rights entry with { Rechte } bitmask
 * @param {number[]} rechteList - Required bitmask flags (e.g. [RECHT_SCHREIBEN])
 * @returns {boolean}
 */
export const hatRecht = (recht, rechteList) =>
    rechteList.every(c => (recht.Rechte & c) > 0)

/**
 * Checks if the logged-in artist has write permission for a target artist.
 * Replaces the former canArtistEditTermin and canArtistEditProjekt functions.
 *
 * @param {number} targetArtistId - The artist whose data should be written
 * @param {Object} loggedInArtist - The logged-in artist object ({ArtistID, isAdmin, CannotEdit})
 * @param {Object} rechteMap - Rights map keyed by target artist ID
 * @returns {boolean}
 */
export const hasWriteRightForArtist = (targetArtistId, loggedInArtist, rechteMap) => {
    if (loggedInArtist?.isAdmin) return true
    if (loggedInArtist?.CannotEdit) return false
    const recht = rechteMap?.[targetArtistId]
    return recht ? hatRecht(recht, [RECHT_SCHREIBEN]) : false
}

export const hasDeleteRightForArtist = (targetArtistId, loggedInArtist, rechteMap) => {
    if (loggedInArtist?.isAdmin) return true
    if (loggedInArtist?.CannotEdit) return false
    const recht = rechteMap?.[targetArtistId]
    return recht ? hatRecht(recht, [RECHT_LOESCHEN]) : false
}

const hasIncomeWritePermission = (recht) => {
    if (!recht) return false
    return Boolean(recht.EinnBearbeiten) || (recht.Rechte & RECHT_EINNAHMENSCHREIBEN) > 0
}

export const hasEditEinnahmenRightForArtist = (targetArtistId, loggedInArtist, rechteMap) => {
    if (loggedInArtist?.isAdmin) return true
    if (loggedInArtist?.CannotEdit) return false
    const recht = rechteMap?.[targetArtistId]
    return hasIncomeWritePermission(recht)
}

/**
 * Checks if the logged-in artist can delete a specific appointment.
 * Admins and the creator of the appointment can always delete.
 *
 * @param {Object} termin - The appointment ({ArtistID, _createdID})
 * @param {Object} loggedInArtist - The logged-in artist object
 * @param {Object} rechteMap - Rights map keyed by target artist ID
 * @returns {boolean}
 */
export const canDeleteTermin = (termin, loggedInArtist, rechteMap) => {
    if (!termin || !loggedInArtist) {
        return false
    }
    if (loggedInArtist.CannotEdit) {
        return false;
    }
    if (loggedInArtist.isAdmin || termin._createdID === loggedInArtist.ArtistID) {
        return true;
    }
    const recht = rechteMap && rechteMap[termin.ArtistID];
    if (recht) {
        return hatRecht(recht, [RECHT_LOESCHEN]);
    }
    return false;
};

/**
 * Checks if the logged-in artist can edit income for a specific target artist.
 * EditIncome is always sourced from the target artist's settings in artistMap,
 * ensuring the correct target artist's flag is used (not the logged-in artist's).
 *
 * @param {number} targetArtistId - The artist whose income editing permission is checked
 * @param {Object} loggedInArtist - The logged-in artist object ({ArtistID, isAdmin, CannotEdit})
 * @param {Object} rechteMap - Rights map keyed by target artist ID
 * @param {Object} artistMap - Artist map containing artist settings (source of EditIncome)
 * @returns {boolean}
 */
export const canEditEinnahmenForArtist = (targetArtistId, loggedInArtist, rechteMap, artistMap) => {
    if (!loggedInArtist || loggedInArtist.CannotEdit) return false
    if (loggedInArtist.isAdmin) return true
    if (targetArtistId === loggedInArtist.ArtistID) return true

    const recht = rechteMap?.[targetArtistId]
    if (!recht) return false

    const hasSchreibrecht = (recht.Rechte & RECHT_SCHREIBEN) > 0
    if (!hasSchreibrecht) return false

    const targetEditIncome = Boolean(artistMap?.[targetArtistId]?.EditIncome)
    if (!targetEditIncome) return true

    return hasIncomeWritePermission(recht)
}

/**
 * Internal helper: filters artistMap to artists where loggedInArtist has the required rights.
 */
const getArtistMapWithAdvancedRights = (artistMap, loggedInArtist, rechteMap, rechteList) => {
    const {isAdmin, CannotEdit} = loggedInArtist
    if (isAdmin) {
        return artistMap
    }
    if (CannotEdit) {
        return {}
    }
    const artistIdListWithRecht = Object.values(rechteMap).filter(recht => hatRecht(recht, rechteList))
        .map(t => t.ArtistID) || []
    return pick(artistMap, artistIdListWithRecht)
}

/**
 * Returns a filtered artistMap containing only artists the logged-in artist can write to.
 *
 * @param {Object} artistMap - Full artist map
 * @param {Object} loggedInArtist - The logged-in artist (with combined isAdmin/CannotEdit flags)
 * @param {Object} rechteMap - Rights map keyed by target artist ID
 * @returns {Object}
 */
export const getArtistMapWithSchreibrecht = (artistMap, loggedInArtist, rechteMap) => {
    if (!artistMap || !loggedInArtist || !rechteMap) {
        return {}
    }
    return getArtistMapWithAdvancedRights(artistMap, loggedInArtist, rechteMap, [RECHT_SCHREIBEN])
}

/**
 * Returns a filtered artistMap containing only artists whose income the logged-in artist can edit.
 *
 * @param {Object} artistMap - Full artist map
 * @param {Object} loggedInArtist - The logged-in artist (with combined isAdmin/CannotEdit flags)
 * @param {Object} rechteMap - Rights map keyed by target artist ID
 * @returns {Object}
 */
export const getArtistMapWithEinnahmenBearbeiten = (artistMap, loggedInArtist, rechteMap) => {
    if (!artistMap || !loggedInArtist || !rechteMap) {
        return {}
    }
    if (loggedInArtist.isAdmin) return artistMap
    if (loggedInArtist.CannotEdit) return {}

    const artistIdListWithRecht = Object.keys(rechteMap)
        .map(Number)
        .filter(artistId => canEditEinnahmenForArtist(artistId, loggedInArtist, rechteMap, artistMap))
    return pick(artistMap, artistIdListWithRecht)
}
